import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { JobworkPaymentsProps } from "../../types/jobworkPayments";
import { JobworkPartiesProps } from "../../types/jobworkParties";

export interface JobworkPaymentsState {
  jobworkParty: JobworkPartiesProps;
  jobworkPayments: JobworkPaymentsProps[];
  totalJobworkPayments: number;
  totalInvoice: number;
  totalPaymnet: number;
  totalNoteAmount: number;
  totalAmount: number;
  isCreateModalOpen: number;
  editJobworkPayment: JobworkPaymentsProps | null;
  loading: boolean;
  error: string | null;
}

const initialState: JobworkPaymentsState = {
  jobworkParty: {} as JobworkPartiesProps,
  jobworkPayments: [],
  totalJobworkPayments: 0,
  totalInvoice: 0,
  totalPaymnet: 0,
  totalNoteAmount: 0,
  totalAmount: 0,
  isCreateModalOpen: 0,
  editJobworkPayment: null,
  loading: false,
  error: null,
};

type FetchJobworkPaymentsListDataProps = {
  id: string;
  queryParams: {
    page: number;
    size?: number;
    fromDate?: string | null;
    toDate?: string | null;
  };
};

export const fetchJobworkPaymentsListData = createAsyncThunk(
  "fetchJobworkPaymentsListData",
  async (req: FetchJobworkPaymentsListDataProps) => {
    let response: any;
    await GET(`/v1/admin/jobwork-payments/${req.id}`, { ...req.queryParams })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const jobworkPaymentsSlice = createSlice({
  name: "jobworkPayments",
  initialState,
  reducers: {
    toggleCreateModal: (state, action: PayloadAction<number>) => {
      state.isCreateModalOpen = action.payload;
    },
    setEditJobworkPayment: (state, action: PayloadAction<JobworkPaymentsProps | null>) => {
      state.editJobworkPayment = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobworkPaymentsListData.pending, (state, action: any) => {
      state.jobworkPayments = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobworkPaymentsListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.jobworkParty = action.payload.data.data.jobwork_party;
        state.jobworkPayments = action.payload.data.data.records;
        state.totalJobworkPayments = action.payload.data.data.total_records;
        state.totalInvoice = action.payload.data.data.total_invoice;
        state.totalPaymnet = action.payload.data.data.total_paymnet;
        state.totalNoteAmount = action.payload.data.data.total_note_amount;
        state.totalAmount = action.payload.data.data.total_amount;
        state.error = null;
      } else {
        state.jobworkParty = {} as JobworkPartiesProps;
        state.jobworkPayments = [];
        state.totalInvoice = 0;
        state.totalPaymnet = 0;
        state.totalNoteAmount = 0;
        state.totalAmount = 0;
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchJobworkPaymentsListData.rejected, (state, action: any) => {
      state.jobworkPayments = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
  },
});

export const { toggleCreateModal, setEditJobworkPayment } = jobworkPaymentsSlice.actions;

export const getJobworkParty = (state: RootState) => state.jobworkPayments.jobworkParty;

export const getJobworkPayments = (state: RootState) => state.jobworkPayments.jobworkPayments;

export const getTotalJobworkPayments = (state: RootState) =>
  state.jobworkPayments.totalJobworkPayments;

export const getTotalInvoice = (state: RootState) =>
  state.jobworkPayments.totalInvoice;

export const getTotalPaymnet = (state: RootState) =>
  state.jobworkPayments.totalPaymnet;

export const getTotalNoteAmount = (state: RootState) =>
  state.jobworkPayments.totalNoteAmount;

export const getTotalAmount = (state: RootState) => state.jobworkPayments.totalAmount;

export const getIsCreateModalOpen = (state: RootState) =>
  state.jobworkPayments.isCreateModalOpen;

export const getEditJobworkPayment = (state: RootState) => state.jobworkPayments.editJobworkPayment;

export default jobworkPaymentsSlice.reducer;
